import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSfx } from '../../store/sfxStore';

interface BackButtonProps {
    to?: string;
    label?: string;
    variant?: 'default' | 'floating' | 'inline';
    onClick?: () => void;
}

/**
 * Styled back button component with consistent design across the app.
 * 
 * Variants:
 * - default: Standard button for page headers
 * - floating: Fixed position overlay button (for modals/certificates)
 * - inline: Compact version for inline use
 */
export const BackButton: React.FC<BackButtonProps> = ({
    to = '/',
    label = 'Back',
    variant = 'default',
    onClick
}) => {
    const navigate = useNavigate();
    const { play } = useSfx();

    const handleClick = () => {
        play('click');
        if (onClick) {
            onClick();
        } else {
            navigate(to);
        }
    };

    const baseClasses = `
        flex items-center gap-2 font-bold transition-all
        focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2
    `;

    const variantClasses = {
        default: `
            px-4 py-2.5 bg-white rounded-xl shadow-md border-2 border-slate-100
            text-slate-600 hover:text-green-600 hover:border-green-200 hover:shadow-lg
            hover:bg-green-50 active:scale-95
        `,
        floating: `
            fixed top-4 left-4 z-50 px-4 py-2.5
            bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border-2 border-white/50
            text-slate-600 hover:text-green-600 hover:bg-green-50 hover:shadow-xl
            print:hidden active:scale-95
        `,
        inline: `
            px-3 py-1.5 rounded-lg text-sm
            text-slate-400 hover:text-green-600 hover:bg-green-50
            active:scale-95
        `
    };

    return (
        <motion.button
            onClick={handleClick}
            onMouseEnter={() => play('hover')}
            className={`${baseClasses} ${variantClasses[variant]}`}
            whileHover={{ x: -3 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
        >
            <motion.div
                animate={{ x: [0, -3, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
            >
                <ArrowLeft size={variant === 'inline' ? 16 : 20} />
            </motion.div>
            <span>{label}</span>
        </motion.button>
    );
};

export default BackButton;
